import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Plus, Minus, X, ArrowRight } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { money } from '@/lib/format';
import { formatPack } from '@/lib/units';

const HIDDEN_PATHS = ['/cart', '/checkout', '/auth', '/auth/callback'];

function CartList({ onItemAction }) {
  const { items, updateQuantity, removeItem, total } = useCart();

  if (!items.length) {
    return (
      <div data-testid="cart-drawer-empty" className="flex flex-col items-center justify-center text-center py-12 px-4 h-full">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <ShoppingBag className="h-8 w-8 text-teal/50" strokeWidth={1.5} />
        </div>
        <p className="font-body text-sm font-medium text-foreground/70 mb-1">Your cart is empty</p>
        <p className="font-body text-xs text-foreground/40">Browse vendors and start adding products</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3" data-testid="cart-drawer-items">
        {items.map((item) => (
          <div
            key={item.product_id}
            data-testid={`cart-drawer-item-${item.product_id}`}
            className="pb-3 border-b border-border/50 last:border-0"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm font-semibold text-foreground truncate">{item.name}</p>
                {formatPack(item) && (
                  <p className="font-body text-xs text-teal mt-0.5 truncate" data-testid={`cart-drawer-pack-${item.product_id}`}>
                    {formatPack(item)}
                  </p>
                )}
                {item.supplier_name && (
                  <p className="font-body text-[10px] uppercase tracking-wider text-gold/70 mt-0.5 truncate">
                    {item.supplier_name}
                  </p>
                )}
              </div>
              <button
                onClick={() => removeItem(item.product_id)}
                className="text-foreground/40 hover:text-red-400 transition-colors flex-shrink-0"
                data-testid={`cart-drawer-remove-${item.product_id}`}
                aria-label="Remove item"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center border border-border rounded-md overflow-hidden">
                <button
                  onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                  className="px-2 py-1 text-foreground/60 hover:text-foreground hover:bg-muted transition-colors"
                  data-testid={`cart-drawer-minus-${item.product_id}`}
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" strokeWidth={2} />
                </button>
                <span className="px-3 py-1 font-body text-sm text-foreground min-w-[2.25rem] text-center">
                  {item.quantity}
                </span>
                <button
                  onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                  className="px-2 py-1 text-foreground/60 hover:text-foreground hover:bg-muted transition-colors"
                  data-testid={`cart-drawer-plus-${item.product_id}`}
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
              <p className="font-body text-sm font-bold text-foreground">
                R{money((item.price * item.quantity))}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-border px-4 sm:px-5 py-4 space-y-3 bg-card">
        <div className="flex items-center justify-between">
          <span className="font-body text-xs uppercase tracking-wider text-foreground/60">Subtotal</span>
          <span className="font-body text-lg font-bold text-gold" data-testid="cart-drawer-subtotal">
            R{money(total)}
          </span>
        </div>
        <Link to="/checkout" onClick={onItemAction} data-testid="cart-drawer-checkout-btn">
          <Button variant="ctaPrimary" className="w-full rounded-full font-body text-sm font-semibold py-5 gap-2">
            Checkout <ArrowRight className="h-5 w-5" strokeWidth={2} />
          </Button>
        </Link>
        <Link
          to="/shop"
          onClick={onItemAction}
          className="block text-center font-body text-xs text-foreground/50 hover:text-foreground transition-colors"
        >
          Continue shopping
        </Link>
      </div>
    </>
  );
}

/**
 * Desktop cart card. Parent controls width / height / stickiness via className
 * so it can be composed below the product-detail panel on the Shop page.
 */
export const CartSidebar = ({ className = '' }) => {
  const { items, itemCount } = useCart();
  return (
    <aside
      data-testid="cart-drawer-desktop"
      className={`flex flex-col bg-card border border-border rounded-2xl overflow-hidden ${className}`}
    >
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-teal" strokeWidth={2} />
          <h2 className="font-body text-sm font-bold uppercase tracking-[0.15em] text-foreground">Your Cart</h2>
        </div>
        {items.length > 0 && (
          <span className="font-body text-xs text-foreground/50">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <CartList />
    </aside>
  );
};

/** Mobile floating pill that opens a right-side Sheet. */
export const CartMobile = () => {
  const { items, itemCount, total } = useCart();
  const [open, setOpen] = useState(false);

  return (
    <div className="lg:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        {items.length > 0 && (
          <SheetTrigger asChild>
            <button
              data-testid="cart-drawer-mobile-pill"
              className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-teal text-white px-4 py-3 rounded-full shadow-2xl hover:bg-teal-dark transition-colors font-body text-sm font-semibold"
              aria-label="Open cart"
            >
              <ShoppingBag className="h-5 w-5" strokeWidth={2} />
              <span data-testid="cart-drawer-mobile-count">{itemCount}</span>
              <span className="text-gold">·</span>
              <span data-testid="cart-drawer-mobile-total">R{total.toFixed(0)}</span>
            </button>
          </SheetTrigger>
        )}
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-background border-l border-border">
          <div className="px-4 py-4 border-b border-border flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-teal" strokeWidth={2} />
            <h2 className="font-body text-sm font-bold uppercase tracking-[0.15em] text-foreground">Your Cart</h2>
          </div>
          <CartList onItemAction={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </div>
  );
};

/**
 * Desktop: sticky right sidebar (lg+).
 * Mobile: floating pill that opens a Sheet from the right.
 */
export const CartDrawer = () => {
  const location = useLocation();

  if (HIDDEN_PATHS.some(p => location.pathname.startsWith(p))) return null;

  return (
    <>
      <CartSidebar className="hidden lg:flex w-[320px] flex-shrink-0 sticky top-32 max-h-[calc(100svh-9rem)] supports-[max-height:calc(100dvh-9rem)]:max-h-[calc(100dvh-9rem)]" />
      <CartMobile />
    </>
  );
};
